export function verdictLabel(t, verdict) {
  if (!verdict) return t("id_card_analysis.verdict.unavailable");
  const key = `id_card_analysis.verdict.${verdict}`;
  const label = t(key);
  return label === key ? verdict : label;
}

export function badgeClassFor(verdict) {
  switch (verdict) {
    case "PASS":
      return "bg-green-100 text-green-800";
    case "REVIEW":
      return "bg-yellow-100 text-yellow-800";
    case "FAIL":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

const DOCUMENT_TYPE_KEYS = ["national_id_card", "passport", "residence_permit", "driving_license", "other"];

export function documentTypeLabel(t, type) {
  if (!type) return "—";
  const key = DOCUMENT_TYPE_KEYS.includes(type) ? type : "other";
  return t(`id_card_analysis.document_type.${key}`);
}

export function roleLabel(t, role) {
  const key = `id_card_analysis.role.${role}`;
  const label = t(key);
  return label === key ? role : label;
}

export function isAnalysisAvailable(analysis) {
  if (!analysis || typeof analysis !== "object") return false;
  if (analysis.error) return false;
  return Boolean(analysis.overallVerdict);
}

function formatFirstNames(firstNames) {
  if (!Array.isArray(firstNames)) return "";
  return firstNames.filter(Boolean).join(" ");
}

export function mapExtractedFields(t, analysis) {
  const extracted = analysis?.extractedIdentity;
  if (!extracted) return [];
  return [
    { label: t("id_card_analysis.extracted_field.documentType"), value: documentTypeLabel(t, extracted.documentType) },
    { label: t("id_card_analysis.extracted_field.lastName"), value: extracted.lastName || "" },
    { label: t("id_card_analysis.extracted_field.firstNames"), value: formatFirstNames(extracted.firstNames) },
    { label: t("id_card_analysis.extracted_field.birthDate"), value: extracted.birthDate || "" },
    { label: t("id_card_analysis.extracted_field.expiryDate"), value: extracted.expiryDate || "" },
  ];
}

const GLOBAL_CHECK_FIELDS = ["documentType", "documentValidity", "policyVsDeclarantConsistency"];

export function mapGlobalChecks(t, analysis) {
  const checks = analysis?.checks;
  if (!checks) return [];
  return GLOBAL_CHECK_FIELDS.filter((field) => checks[field]).map((field) => ({
    field,
    label: t(`id_card_analysis.global_check.${field}`),
    verdict: checks[field]?.verdict,
    reason: checks[field]?.reason,
    extractedValue: checks[field]?.extractedValue || "",
    expectedValue: checks[field]?.expectedValue || "",
  }));
}

const ROLE_TO_CHECKS_KEY = {
  policy_holder: "policyHolder",
  declarant: "declarant",
};

const PERSON_FIELD_KEYS = ["lastName", "firstName", "birthDate", "fullName"];

export function mapPersonChecks(t, analysis, role) {
  const roleKey = ROLE_TO_CHECKS_KEY[role];
  if (!roleKey) return [];
  const personChecks = analysis?.checks?.[roleKey];
  if (!personChecks) return [];
  return PERSON_FIELD_KEYS.filter((field) => personChecks[field]).map((field) => ({
    field,
    label: t(`id_card_analysis.person_field.${field}`),
    verdict: personChecks[field]?.verdict,
    reason: personChecks[field]?.reason,
    extractedValue: personChecks[field]?.extractedValue || "",
    expectedValue: personChecks[field]?.expectedValue || "",
  }));
}

export function mapAnomalies(analysis) {
  const anomalies = analysis?.anomalies;
  if (!Array.isArray(anomalies)) return [];
  return anomalies.filter(Boolean);
}

export function isSamePerson(analysis) {
  return analysis?.checks?.policyVsDeclarantConsistency?.samePerson === true;
}

export function formatAnalyzedAt(analyzedAt) {
  if (!analyzedAt) return "";
  const date = new Date(analyzedAt);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("fr-FR");
}

export function formatIdCardBadge(t, { analyzedAt, analysis } = {}) {
  if (analysis?.error) {
    return { label: t("id_card_analysis.badge.unavailable"), className: "bg-red-100 text-red-800" };
  }
  if (!analyzedAt) {
    return { label: t("id_card_analysis.badge.not_analyzed"), className: "bg-gray-200 text-gray-700" };
  }
  const verdict = analysis?.overallVerdict || null;
  const anomaliesCount = mapAnomalies(analysis).length;
  const baseLabel = verdictLabel(t, verdict);
  const label =
    anomaliesCount > 0 ? `${baseLabel} (${t("id_card_analysis.badge.anomalies", anomaliesCount)})` : baseLabel;
  return { label, className: badgeClassFor(verdict) };
}
